import React from "react";
import { useTranslation } from "react-i18next";
import StatisticCard from "../../stats/components/StatisticCard";
import PercentileChart from "../../stats/components/PercentileChart";
import ProUpgradeCard from "./ProUpgradeCard";
import { formatValue } from "../../../utils/common";

const StatisticsSection = ({ session, proMember }) => {
  const { t } = useTranslation();

  const renderPercentileCard = (
    title,
    hintText,
    lowerKey,
    medianKey,
    upperKey
  ) => {
    if (!proMember) {
      return <ProUpgradeCard title={title} />;
    }

    return (
      <StatisticCard title={title} hint={hintText}>
        <div className="bg-primary/30 rounded-lg p-3 mb-2sm:p-1">
          <PercentileChart
            lowerPercentile={formatValue(session.statistics[lowerKey])}
            averagePercentile={formatValue(session.statistics[medianKey])}
            upperPercentile={formatValue(session.statistics[upperKey])}
          />
        </div>
      </StatisticCard>
    );
  };

  const renderComparisonCard = (
    title,
    comparisonKey,
    likelihoodKey,
    avgDiffKey
  ) => {
    if (!proMember) {
      return <ProUpgradeCard title={title} />;
    }

    const comparisonValue = session.statistics[comparisonKey];
    const isInsignificant = comparisonValue?.toLowerCase() === "insignificant";

    let hintText;
    if (avgDiffKey && !isInsignificant) {
      hintText = `Going for ${comparisonValue} increases your 501 Average by ${
        session.statistics[avgDiffKey]?.toFixed(1) || "-"
      }.`;
    } else if (likelihoodKey && !isInsignificant) {
      hintText = `Going for ${comparisonValue} darts increases your chance to hit the double by ${formatValue(
        session.statistics[likelihoodKey]
      )}%.`;
    } else {
      hintText = avgDiffKey
        ? "No significant difference going for T20 or T19."
        : "It does not make any difference for your checkout chance, if you go for D20 or D16.";
    }

    return (
      <StatisticCard title={title} hint={hintText}>
        <div className="bg-primary/30 rounded-lg p-3 sm:p-1 flex flex-col items-center justify-center h-[100px]">
          {isInsignificant ? (
            <p className="text-lg sm:text-xl text-white font-semibold min-w-[60px] text-center">
              Insignificant
            </p>
          ) : (
            <>
              <p className="text-xl sm:text-2xl text-white font-semibold min-w-[60px] text-center">
                {comparisonValue}
              </p>
              {avgDiffKey && (
                <p className="mt-2 text-xs text-white/80">
                  Increases 501-Average by:{" "}
                  <span className="text-test font-medium">
                    {session.statistics[avgDiffKey]?.toFixed(1) || "-"}
                  </span>
                </p>
              )}
              {likelihoodKey && (
                <p className="mt-2 text-xs text-white/80">
                  Accuracy Boost:{" "}
                  <span className="text-test font-medium">
                    {formatValue(session.statistics[likelihoodKey])}%
                  </span>
                </p>
              )}
            </>
          )}
        </div>
      </StatisticCard>
    );
  };

  const renderBasicStatCard = (title, statisticKey, hint) => (
    <StatisticCard title={title} hint={hint}>
      <div className="bg-primary/30 rounded-lg p-3 sm:p-1 flex flex-col items-center justify-center">
        <p className="text-lg sm:text-xl text-white font-semibold min-w-[60px] text-center">
          {typeof session.statistics[statisticKey] === "number"
            ? formatValue(session.statistics[statisticKey])
            : "-"}
        </p>
      </div>
    </StatisticCard>
  );

  return (
    <div className="space-y-2 sm:space-y-4">
      {/* Percentile Charts */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4">
        <div className="statistic-card-container min-h-[150px] h-full flex flex-col justify-center p-1">
          {renderPercentileCard(
            "501 Average",
            `In a 501 game you usually average ${formatValue(
              session.statistics["avg_median"]
            )}. Most of the time your average is between ${formatValue(
              session.statistics["avg_lower"]
            )} and ${formatValue(session.statistics["avg_upper"])}.`,
            "avg_lower",
            "avg_median",
            "avg_upper"
          )}
        </div>

        <div className="statistic-card-container min-h-[150px] h-full flex flex-col justify-center p-1">
          {renderPercentileCard(
            "Double Hit",
            `Usually you hit the final double using ${formatValue(
              session.statistics["dq_median"]
            )} darts. Most of the time you need between ${formatValue(
              session.statistics["dq_lower"]
            )} and ${formatValue(session.statistics["dq_upper"])} darts.`,
            "dq_lower",
            "dq_median",
            "dq_upper"
          )}
        </div>

        <div className="statistic-card-container min-h-[150px] h-full flex flex-col justify-center p-1">
          {renderBasicStatCard(
            t("history.gameHistory.score"),
            "general_score",
            t("history.gameHistory.scoreHint")
          )}
        </div>
      </div>

      {/* Comparison Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4">
        <div className="statistic-card-container min-h-[150px] h-full flex flex-col justify-center p-1">
          {renderComparisonCard(
            "D 20 vs. D 16",
            "D20_or_D16",
            "D20_or_D16_likelihood"
          )}
        </div>

        <div className="statistic-card-container min-h-[150px] h-full flex flex-col justify-center p-1">
          {renderComparisonCard(
            "T 20 vs. T 19",
            "T20_or_T19",
            null,
            "T20_or_T19_avg_difference"
          )}
        </div>

        <div className="statistic-card-container min-h-[150px] h-full flex flex-col justify-center p-1">
          {renderBasicStatCard(
            t("history.gameHistory.doubleHit"),
            "double_hit",
            t("history.gameHistory.doubleHitHint")
          )}
        </div>
      </div>
    </div>
  );
};

export default StatisticsSection;
